import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FmButton } from '@/components/ui/FmButton';
import {
  PlanAssistantRequestError,
  type PlanAssistantAsk,
  type PlanAssistantConversationContext,
  type PlanAssistantIntent,
  type PlanAssistantRequest,
  type PlanAssistantResponse,
} from '@/contexts/planAssistant.types';

interface Turn {
  id: string;
  question: string;
  response: PlanAssistantResponse['response'];
}

interface StarterQuestion {
  question: string;
  intentIds: PlanAssistantIntent[];
}

const FI_STARTERS: StarterQuestion[] = [
  {
    question: 'Mihin tämän suunnitelman luvut perustuvat?',
    intentIds: ['data_quality.explain'],
  },
  {
    question: 'Mitä keskiläpimitta kertoo — ja mitä se ei kerro?',
    intentIds: ['data_quality.explain'],
  },
  {
    question: 'Miten diskonttokorko vaikuttaa vertailuun?',
    intentIds: ['economics.explain'],
  },
  {
    question: 'Miten puuston arvo muuttuu?',
    intentIds: ['timing.explain', 'economics.explain'],
  },
  {
    question: 'Mitä minun kannattaa tarkistaa maastossa ennen päätöstä?',
    intentIds: ['data_quality.explain'],
  },
];

const EN_STARTERS: StarterQuestion[] = [
  {
    question: 'What are the figures in this plan based on?',
    intentIds: ['data_quality.explain'],
  },
  {
    question: 'What does mean diameter tell me — and what does it not tell me?',
    intentIds: ['data_quality.explain'],
  },
  {
    question: 'How does the discount rate affect the comparison?',
    intentIds: ['economics.explain'],
  },
  {
    question: 'How does the value of the growing stock change?',
    intentIds: ['timing.explain', 'economics.explain'],
  },
  {
    question: 'What should I verify in the field before deciding?',
    intentIds: ['data_quality.explain'],
  },
];

function requestFailure(caught: unknown, finnish: boolean) {
  const code = caught instanceof PlanAssistantRequestError ? caught.code : null;
  const revisionChanged = code === 'plan_revision_changed';
  const conversationExpired = revisionChanged || code === 'invalid_plan_conversation';
  if (revisionChanged) {
    return {
      revisionChanged,
      conversationExpired,
      message: finnish
        ? 'Suunnitelma ehti muuttua. Päivitämme valitun suunnitelman ennen seuraavaa kysymystä.'
        : 'The plan changed. We are refreshing the selected plan before the next question.',
    };
  }
  if (conversationExpired) {
    return {
      revisionChanged,
      conversationExpired,
      message: finnish
        ? 'Suunnitelma tai keskustelu ehti muuttua. Kysymyksesi säilyi, ja seuraava yritys aloittaa uuden keskustelun.'
        : 'The plan or conversation changed. Your question is preserved, and the next attempt starts a new conversation.',
    };
  }
  return {
    revisionChanged,
    conversationExpired,
    message: finnish
      ? 'Vastausta ei saatu. Kysymyksesi säilyi — yritä hetken kuluttua uudelleen.'
      : 'No answer was received. Your question is still here — try again shortly.',
  };
}

function assistantRequest(args: {
  context: PlanAssistantConversationContext;
  question: string;
  intentIds?: PlanAssistantIntent[];
  continuationToken: string | null;
}): PlanAssistantRequest {
  const { context, question, intentIds, continuationToken } = args;
  return {
    plan_reference_kind: context.planReferenceKind,
    property_id: context.propertyId,
    plan_id: context.planId,
    plan_revision: context.planRevision,
    question,
    ...(intentIds === undefined ? {} : { intent_ids: intentIds }),
    ...(continuationToken === null ? {} : { continuation_token: continuationToken }),
  };
}

function usePlanAssistantSubmit(args: {
  context: PlanAssistantConversationContext;
  ask: PlanAssistantAsk;
  onContextExpired?: () => void;
  finnish: boolean;
  draft: string;
  continuationToken: string | null;
  requestPendingRef: React.RefObject<boolean>;
  requestAbortRef: React.RefObject<AbortController | null>;
  draftRevisionRef: React.RefObject<number>;
  setDraft: React.Dispatch<React.SetStateAction<string>>;
  setDraftIntentIds: React.Dispatch<React.SetStateAction<PlanAssistantIntent[] | undefined>>;
  setTurns: React.Dispatch<React.SetStateAction<Turn[]>>;
  setContinuationToken: React.Dispatch<React.SetStateAction<string | null>>;
  setSending: React.Dispatch<React.SetStateAction<boolean>>;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
}): (question: string, intentIds?: PlanAssistantIntent[]) => Promise<void> {
  const {
    context,
    ask,
    onContextExpired,
    finnish,
    draft,
    continuationToken,
    requestPendingRef,
    requestAbortRef,
    draftRevisionRef,
    setDraft,
    setDraftIntentIds,
    setTurns,
    setContinuationToken,
    setSending,
    setError,
  } = args;

  return async (question: string, intentIds?: PlanAssistantIntent[]) => {
    const trimmed = question.trim();
    if (!trimmed || requestPendingRef.current) return;
    const draftRevisionAtStart = draftRevisionRef.current;
    const submittedFromDraft = question === draft;
    const abortController = new AbortController();
    requestAbortRef.current = abortController;
    requestPendingRef.current = true;
    setSending(true);
    setError(null);
    try {
      const result = await ask(
        assistantRequest({ context, question: trimmed, intentIds, continuationToken }),
        abortController.signal,
      );
      if (abortController.signal.aborted) return;
      setTurns((current) => [
        ...current,
        { id: result.continuation_token, question: trimmed, response: result.response },
      ]);
      setContinuationToken(result.continuation_token);
      if (submittedFromDraft && draftRevisionRef.current === draftRevisionAtStart) {
        setDraft('');
        setDraftIntentIds(undefined);
      }
    } catch (caught) {
      if (abortController.signal.aborted) return;
      const failure = requestFailure(caught, finnish);
      if (failure.conversationExpired) {
        setContinuationToken(null);
        setTurns([]);
      }
      if (draftRevisionRef.current === draftRevisionAtStart) {
        setDraft(trimmed);
        setDraftIntentIds(intentIds);
      }
      if (failure.revisionChanged) onContextExpired?.();
      setError(failure.message);
    } finally {
      if (requestAbortRef.current === abortController) requestAbortRef.current = null;
      requestPendingRef.current = false;
      if (!abortController.signal.aborted) setSending(false);
    }
  };
}

export const PlanAssistantConversation: React.FC<{
  context: PlanAssistantConversationContext;
  ask: PlanAssistantAsk;
  onContextExpired?: () => void;
}> = ({ context, ask, onContextExpired }) => {
  const finnish = context.locale.toLowerCase().startsWith('fi');
  const starters = useMemo(() => (finnish ? FI_STARTERS : EN_STARTERS), [finnish]);
  const [draft, setDraft] = useState('');
  const [draftIntentIds, setDraftIntentIds] = useState<PlanAssistantIntent[] | undefined>();
  const [turns, setTurns] = useState<Turn[]>([]);
  const [continuationToken, setContinuationToken] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestPendingRef = useRef(false);
  const requestAbortRef = useRef<AbortController | null>(null);
  const draftRevisionRef = useRef(0);

  useEffect(
    () => () => {
      requestAbortRef.current?.abort();
      requestAbortRef.current = null;
    },
    [],
  );

  const submit = usePlanAssistantSubmit({
    context,
    ask,
    onContextExpired,
    finnish,
    draft,
    continuationToken,
    requestPendingRef,
    requestAbortRef,
    draftRevisionRef,
    setDraft,
    setDraftIntentIds,
    setTurns,
    setContinuationToken,
    setSending,
    setError,
  });

  return (
    <div className="plan-assistant" data-testid="plan-assistant-conversation">
      {turns.length === 0 ? (
        <div
          className="plan-assistant__starters"
          aria-label={finnish ? 'Aloituskysymykset' : 'Starter questions'}
        >
          {starters.map((starter) => (
            <FmButton
              key={starter.question}
              variant="bordered"
              onClick={() => void submit(starter.question, starter.intentIds)}
            >
              {starter.question}
            </FmButton>
          ))}
        </div>
      ) : null}
      <div className="plan-assistant__turns" aria-live="polite">
        {turns.map((turn) => (
          <article key={turn.id}>
            <p className="plan-assistant__question">{turn.question}</p>
            <p>{turn.response.answer}</p>
            {turn.response.limitations.length ? (
              <ul>
                {turn.response.limitations.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            ) : null}
            {turn.response.citations.length ? (
              <div className="plan-assistant__sources">
                <strong>{finnish ? 'Lähteet' : 'Sources'}</strong>
                {turn.response.citations.map((citation) =>
                  citation.url ? (
                    <a
                      key={`${citation.title}-${citation.url}`}
                      href={citation.url}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {citation.title}
                      {citation.locator ? ` · ${citation.locator}` : ''}
                    </a>
                  ) : (
                    <span key={`${citation.title}-${citation.locator}`}>{citation.title}</span>
                  ),
                )}
              </div>
            ) : null}
            {turn.response.follow_up_questions.map((followUp) => (
              <FmButton key={followUp} variant="text" onClick={() => void submit(followUp)}>
                {followUp}
              </FmButton>
            ))}
          </article>
        ))}
      </div>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void submit(draft, draftIntentIds);
        }}
      >
        <label htmlFor="plan-assistant-question">
          {finnish ? 'Kysy suunnitelmasta' : 'Ask about the plan'}
        </label>
        <textarea
          id="plan-assistant-question"
          value={draft}
          onChange={(event) => {
            draftRevisionRef.current += 1;
            setDraft(event.target.value);
            setDraftIntentIds(undefined);
          }}
          maxLength={500}
          rows={3}
        />
        {error ? <p role="alert">{error}</p> : null}
        <FmButton type="submit" variant="primary" disabled={sending || !draft.trim()}>
          {sending
            ? finnish
              ? 'Haetaan ja vastataan…'
              : 'Retrieving and answering…'
            : finnish
              ? 'Kysy'
              : 'Ask'}
        </FmButton>
      </form>
    </div>
  );
};
